import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../features/store';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { createOrder } from '../features/orders';
import { selectOrdersLoading, selectOrdersError } from '../features/orders';
import CheckoutHeader from './Checkout/components/CheckoutHeader';
import ShippingSection, { ShippingAddress } from './Checkout/components/ShippingSection';
import BillingSection, { BillingAddress } from './Checkout/components/BillingSection';
import PaymentSection, { PaymentInfo } from './Checkout/components/PaymentSection';
import OrderSummarySidebar from './Checkout/components/OrderSummarySidebar';
import CheckoutNavigation from './Checkout/components/CheckoutNavigation';
import './Checkout.css';
import Loader from '../components/Loader';

const Checkout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const { cartItems, getTotalPrice } = useCart();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const ordersLoading = useSelector(selectOrdersLoading);
  const ordersError = useSelector(selectOrdersError);

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'IL',
    phone: '',
  });

  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    sameAsShipping: true,
    firstName: '',
    lastName: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'IL',
  });

  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    saveCard: false,
    agreeToTerms: false,
  });

  const [shippingMethod, setShippingMethod] = useState('standard');
  const [orderNotes, setOrderNotes] = useState('');
  const [hypPaymentUrl, setHypPaymentUrl] = useState<string | null>(null);
  const [hypLoading, setHypLoading] = useState(false);
  const [hypError, setHypError] = useState<string | null>(null);
  const hypStartedRef = useRef(false);

  useEffect(() => {
    if (cartItems.length === 0 && !hypPaymentUrl) {
      navigate('/cart');
    }
  }, [cartItems, navigate, hypPaymentUrl]);

  const getShippingCost = () => {
    switch (shippingMethod) {
      case 'express':
        return 15.99;
      case 'priority':
        return 9.99;
      default:
        return 4.99;
    }
  };

  const getTax = () => getTotalPrice() * 0.08;
  const getTotal = () => getTotalPrice() + getShippingCost() + getTax();

  const startHypPayment = useCallback(async () => {
    if (hypStartedRef.current || hypPaymentUrl) return;
    hypStartedRef.current = true;
    setHypLoading(true);
    setHypError(null);

    try {
      const subtotal = getTotalPrice();
      const totalPrice = subtotal + getShippingCost() + getTax();
      const clientName = `${shippingAddress.firstName} ${shippingAddress.lastName}`.trim();
      const addressParts = [
        shippingAddress.address,
        shippingAddress.apartment,
        shippingAddress.city,
        shippingAddress.state,
        shippingAddress.zipCode,
        shippingAddress.country,
      ].filter(Boolean);

      const items = cartItems.map((item) => ({
        productId: item.product._id,
        quantity: item.quantity,
        changedPrice:
          typeof item.product.price === 'string'
            ? item.product.price
            : item.product.price.toString(),
        discountGiven: item.product.sale ? 0.2 : 0,
      }));

      const result = await dispatch(
        createOrder({
          clientName,
          clientFirstName: shippingAddress.firstName,
          clientLastName: shippingAddress.lastName,
          phoneNumber: shippingAddress.phone,
          address: addressParts.join(', '),
          street: shippingAddress.address,
          city: shippingAddress.city,
          totalPrice: totalPrice.toString(),
          notes: orderNotes || undefined,
          paymentType: 'credit',
          pageLang: i18n.language === 'en' ? 'ENG' : 'HEB',
          items,
        })
      );

      if (createOrder.fulfilled.match(result) && result.payload.paymentUrl) {
        setHypPaymentUrl(result.payload.paymentUrl);
        // Persist order number so success page can recover if needed
        const orderNumber = result.payload.order.orderNumber;
        if (orderNumber != null) {
          sessionStorage.setItem('hypPendingOrder', String(orderNumber));
        }
      } else {
        hypStartedRef.current = false;
        const msg =
          (createOrder.rejected.match(result) && result.payload) ||
          t('checkout.errors.paymentFailed');
        setHypError(String(msg));
      }
    } catch (error) {
      console.error('Hyp start error:', error);
      hypStartedRef.current = false;
      setHypError(t('checkout.errors.paymentFailed'));
    } finally {
      setHypLoading(false);
    }
  }, [
    hypPaymentUrl,
    cartItems,
    shippingAddress,
    orderNotes,
    dispatch,
    i18n.language,
    getTotalPrice,
    t,
  ]);

  const handleShippingChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setShippingAddress((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBillingChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'sameAsShipping') {
      const checked = (e.target as HTMLInputElement).checked;
      setBillingAddress((prev) => ({
        ...prev,
        sameAsShipping: checked,
        ...(checked
          ? {
              firstName: shippingAddress.firstName,
              lastName: shippingAddress.lastName,
              address: shippingAddress.address,
              apartment: shippingAddress.apartment,
              city: shippingAddress.city,
              state: shippingAddress.state,
              zipCode: shippingAddress.zipCode,
              country: shippingAddress.country,
            }
          : {}),
      }));
    } else {
      setBillingAddress((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (step === 1) {
      if (!shippingAddress.firstName)
        newErrors.firstName = t('checkout.errors.firstNameRequired');
      if (!shippingAddress.lastName)
        newErrors.lastName = t('checkout.errors.lastNameRequired');
      if (!shippingAddress.address)
        newErrors.address = t('checkout.errors.addressRequired');
      if (!shippingAddress.city)
        newErrors.city = t('checkout.errors.cityRequired');
      if (!shippingAddress.zipCode)
        newErrors.zipCode = t('checkout.errors.zipCodeRequired');
      if (!shippingAddress.phone)
        newErrors.phone = t('checkout.errors.phoneRequired');
    }

    if (step === 2) {
      if (!billingAddress.sameAsShipping) {
        if (!billingAddress.firstName)
          newErrors.billingFirstName = t('checkout.errors.firstNameRequired');
        if (!billingAddress.lastName)
          newErrors.billingLastName = t('checkout.errors.lastNameRequired');
        if (!billingAddress.address)
          newErrors.billingAddress = t('checkout.errors.addressRequired');
        if (!billingAddress.city)
          newErrors.billingCity = t('checkout.errors.cityRequired');
        if (!billingAddress.zipCode)
          newErrors.billingZipCode = t('checkout.errors.zipCodeRequired');
      }
    }

    if (step === 3) {
      if (!paymentInfo.agreeToTerms)
        newErrors.agreeToTerms = t('checkout.errors.agreeToTermsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAgreeToTermsChange = (checked: boolean) => {
    setPaymentInfo((prev) => ({ ...prev, agreeToTerms: checked }));
    if (errors.agreeToTerms) {
      setErrors((prev) => ({ ...prev, agreeToTerms: '' }));
    }
    if (checked) {
      const shippingOk = validateStep(1);
      if (!shippingOk) {
        setPaymentInfo((prev) => ({ ...prev, agreeToTerms: false }));
        setCurrentStep(1);
        return;
      }
      const billingOk = validateStep(2);
      if (!billingOk) {
        setPaymentInfo((prev) => ({ ...prev, agreeToTerms: false }));
        setCurrentStep(2);
        return;
      }
      startHypPayment();
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handlePrevious = () => {
    if (hypPaymentUrl) return; // lock back while paying
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  if (cartItems.length === 0 && !hypPaymentUrl) {
    return null;
  }

  const showNav = currentStep < 3 || (!hypPaymentUrl && !hypLoading);

  return (
    <div className={`checkout-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="checkout-container">
        {ordersLoading && !hypPaymentUrl ? (
          <Loader size="large" text={t('checkout.hypLoading')} fullscreen />
        ) : (
          <>
            <CheckoutHeader currentStep={currentStep} />

            <div className="checkout-content">
              <div className="checkout-form">
                {(errors.general || ordersError) && (
                  <div className="error-message general-error">
                    {errors.general || ordersError}
                  </div>
                )}

                {currentStep === 1 && (
                  <ShippingSection
                    shippingAddress={shippingAddress}
                    shippingMethod={shippingMethod}
                    errors={errors}
                    onShippingChange={handleShippingChange}
                    onShippingMethodChange={setShippingMethod}
                  />
                )}

                {currentStep === 2 && (
                  <BillingSection
                    billingAddress={billingAddress}
                    errors={errors}
                    onBillingChange={handleBillingChange}
                  />
                )}

                {currentStep === 3 && (
                  <PaymentSection
                    paymentInfo={paymentInfo}
                    errors={errors}
                    orderNotes={orderNotes}
                    onOrderNotesChange={setOrderNotes}
                    onAgreeToTermsChange={handleAgreeToTermsChange}
                    hypPaymentUrl={hypPaymentUrl}
                    hypLoading={hypLoading || ordersLoading}
                    hypError={hypError}
                  />
                )}

                {showNav && (
                  <CheckoutNavigation
                    currentStep={currentStep}
                    totalSteps={3}
                    isLoading={ordersLoading || hypLoading}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                )}
              </div>

              <OrderSummarySidebar
                shippingMethod={shippingMethod}
                getShippingCost={getShippingCost}
                getTotalPrice={getTotalPrice}
                getTax={getTax}
                getTotal={getTotal}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Checkout;
